import type {
  AccountingTag,
  ClassifiedEvent,
  EventType,
  LegRole,
  OperationCategory,
} from '../types.js';

// Runtime mirrors of the domain enum types (types.ts), the single arrays every
// z.enum() schema derives from so unions and schemas can never drift apart.
export const EVENT_TYPES = [
  'DEPOSIT',
  'WITHDRAWAL',
  'SWAP_OUT',
  'SWAP_IN',
  'BRIDGE_OUT',
  'BRIDGE_IN',
  'GAS_FEE',
  'INTERNAL_TRANSFER',
  'UNKNOWN',
  'IGNORED',
  'SPAM',
] as const satisfies readonly EventType[];

export const OPERATION_CATEGORIES = [
  'DEPOSIT',
  'WITHDRAWAL',
  'SPOT_TRADE',
  'CLOSING',
] as const satisfies readonly OperationCategory[];

export const LEG_ROLES = [
  'HOLDING_DR',
  'HOLDING_CR',
  'HOLDING',
  'STAKED',
  'INTERNAL',
  'WAIT',
  'CLIENT',
  'SUPPLIER',
  'FEE',
  'INCOME_AIRDROP',
  'INCOME_STAKING',
  'PMV_LOSS',
  'PMV_GAIN',
  'CLOSING_HOLDING',
  'CLOSING_LOSS_ADJUSTMENT',
  'CLOSING_GAIN_ADJUSTMENT',
  'CLOSING_PROVISION',
  'CLOSING_PROVISION_EXPENSE',
  'CLOSING_PROVISION_REVERSAL',
] as const satisfies readonly LegRole[];

// Event types that move quantity / cost basis / value — the set that positions,
// value-over-time, price refresh/backfill and closing snapshots must all agree
// on. Single source of truth so they can never drift apart.
export const IMPACTING_EVENT_TYPES: ReadonlySet<EventType> = new Set<EventType>([
  'DEPOSIT',
  'WITHDRAWAL',
  'SWAP_OUT',
  'SWAP_IN',
  'BRIDGE_OUT',
  'BRIDGE_IN',
  'GAS_FEE',
]);

export function isAccountingMovement(event: Pick<ClassifiedEvent, 'eventType' | 'tag'>): boolean {
  return (
    IMPACTING_EVENT_TYPES.has(event.eventType) &&
    !(
      event.tag === 'INTERWALLET' &&
      (event.eventType === 'DEPOSIT' || event.eventType === 'WITHDRAWAL')
    )
  );
}

export const ACCOUNTING_TAGS = [
  'DEFAULT',
  'COMPTE_CLIENT',
  'COMPTE_FOURNISSEUR',
  'BANQUE',
  'INTERWALLET',
  'BRIDGE',
  'AIRDROP',
  'STAKING',
  'STAKING_MINAGE',
  'STAKING_DELEGATE',
  'STAKING_UNDELEGATE',
  'FEE',
  'FEE_IN',
] as const satisfies readonly AccountingTag[];

// STAKING_DELEGATE / STAKING_UNDELEGATE stay in ACCOUNTING_TAGS so historical
// rows still parse, but are never offered: the CUMP engine hard-rejects staked
// principal (UnsupportedStakingEventError) pending EC qualification.
export const ALLOWED_TAGS: Record<OperationCategory, readonly AccountingTag[]> = {
  DEPOSIT: [
    'DEFAULT',
    'COMPTE_CLIENT',
    'BANQUE',
    'INTERWALLET',
    'BRIDGE',
    'AIRDROP',
    'STAKING',
    'STAKING_MINAGE',
    'FEE_IN',
  ],
  WITHDRAWAL: ['DEFAULT', 'BANQUE', 'INTERWALLET', 'BRIDGE', 'COMPTE_FOURNISSEUR', 'FEE'],
  SPOT_TRADE: ['DEFAULT'],
  CLOSING: ['DEFAULT'],
};

export class NonAccountingEventTypeError extends Error {
  override readonly name = 'NonAccountingEventTypeError';
  constructor(eventType: EventType) {
    super(`categoryForEvent: ${eventType} is not an accounting event type`);
  }
}

export function categoryForEvent(eventType: EventType, direction: 1 | -1): OperationCategory {
  switch (eventType) {
    case 'DEPOSIT':
      return 'DEPOSIT';
    case 'WITHDRAWAL':
    case 'GAS_FEE':
      return 'WITHDRAWAL';
    case 'SWAP_IN':
    case 'SWAP_OUT':
      return 'SPOT_TRADE';
    case 'BRIDGE_OUT':
      return 'WITHDRAWAL';
    case 'BRIDGE_IN':
      return 'DEPOSIT';
    case 'INTERNAL_TRANSFER':
      return direction === 1 ? 'DEPOSIT' : 'WITHDRAWAL';
    default:
      throw new NonAccountingEventTypeError(eventType);
  }
}

export function forcedTagForEvent(eventType: EventType): AccountingTag | null {
  if (eventType === 'GAS_FEE') return 'FEE';
  if (eventType === 'BRIDGE_OUT' || eventType === 'BRIDGE_IN') return 'BRIDGE';
  return null;
}
